import { useState, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  Tabs,
  Tab,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ExcelJS from "exceljs";
import { createAndAddCombinedUsersData } from "../axios/axiosFunctions";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const RELATIONSHIP_LABEL_TO_VALUE = {
  manager: "manager",
  "direct report": "direct",
  direct: "direct",
  peer: "peer",
  "other colleague": "otherColleague",
  othercolleague: "otherColleague",
  other: "otherColleague",
};

const RELATIONSHIP_DISPLAY = {
  manager: "Manager",
  direct: "Direct Report",
  peer: "Peer",
  otherColleague: "Other Colleague",
};

const TYPE_LABEL_TO_VALUE = {
  reviewee: "reviewee",
  reviewer: "reviewer",
};

const TEMPLATE_REVIEWEE_ROWS = 5;
const TEMPLATE_REVIEWER_ROWS = 15;

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CsvUploadCombined360 = ({ open, onClose, groupId, slug, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [parsedReviewees, setParsedReviewees] = useState([]);
  const [parsedReviewers, setParsedReviewers] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [showAlert, setShowAlert] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [invalidRows, setInvalidRows] = useState([]);

  const downloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "K2G Assessments";
    workbook.created = new Date();
    const worksheet = workbook.addWorksheet("360 Bulk Upload");

    worksheet.columns = [
      { header: "type", key: "type", width: 14 },
      { header: "firstName", key: "firstName", width: 20 },
      { header: "lastName", key: "lastName", width: 20 },
      { header: "email", key: "email", width: 32 },
      { header: "revieweeEmail", key: "revieweeEmail", width: 32 },
      { header: "relationshipType", key: "relationshipType", width: 22 },
    ];

    worksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F5385" },
    };
    worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };

    worksheet.addRow({ type: "reviewee", firstName: "Jane", lastName: "Smith", email: "jane.smith@example.com" });
    worksheet.addRow({ type: "reviewee", firstName: "John", lastName: "Doe", email: "john.doe@example.com" });

    worksheet.addRow({
      type: "reviewer",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      revieweeEmail: "jane.smith@example.com",
      relationshipType: "Manager",
    });

    for (let i = 0; i < TEMPLATE_REVIEWEE_ROWS; i++) {
      worksheet.addRow({ type: "reviewee" });
    }
    for (let i = 0; i < TEMPLATE_REVIEWER_ROWS; i++) {
      worksheet.addRow({ type: "reviewer" });
    }

    const lastRow = worksheet.rowCount;
    for (let r = 2; r <= lastRow; r++) {
      worksheet.getCell(`A${r}`).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: ['"reviewee,reviewer"'],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid type",
        error: "Type must be either 'reviewee' or 'reviewer'",
      };
      worksheet.getCell(`F${r}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"Manager,Direct Report,Peer,Other Colleague"'],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid relationship",
        error: "Use Manager, Direct Report, Peer, or Other Colleague",
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "360_assessment_template.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizeRelationship = (value) => {
    if (!value) return null;
    const normalized = String(value).toLowerCase().trim();
    return RELATIONSHIP_LABEL_TO_VALUE[normalized] || null;
  };

  const normalizeType = (value) => {
    if (!value) return null;
    const normalized = String(value).toLowerCase().trim();
    return TYPE_LABEL_TO_VALUE[normalized] || null;
  };

  const cellText = (cell) => {
    if (cell == null) return "";
    const v = cell.value;
    if (v == null) return "";
    if (typeof v === "object") {
      if (v.text) return String(v.text).trim();
      if (v.result) return String(v.result).trim();
      if (Array.isArray(v.richText)) {
        return v.richText.map((rt) => rt.text || "").join("").trim();
      }
      return "";
    }
    return String(v).trim();
  };

  const parseXlsx = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      return {
        reviewees: [],
        reviewers: [],
        errors: ["Workbook has no worksheets"],
        invalidRows: [],
      };
    }

    const reviewees = [];
    const reviewers = [];
    const parseErrors = [];
    const invalidRows = [];
    const seenRevieweeEmails = new Set();
    const seenReviewerEmails = new Set();
    const revieweeEmailMap = new Map();

    const rowsRaw = [];
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const typeRaw = cellText(row.getCell(1));
      const firstName = cellText(row.getCell(2));
      const lastName = cellText(row.getCell(3));
      const email = cellText(row.getCell(4));
      const revieweeEmail = cellText(row.getCell(5));
      const relationshipType = cellText(row.getCell(6));

      if (!typeRaw && !firstName && !lastName && !email && !revieweeEmail && !relationshipType) {
        return;
      }
      rowsRaw.push({
        rowNumber,
        typeRaw,
        firstName,
        lastName,
        email,
        revieweeEmail,
        relationshipType,
      });
    });

    for (const r of rowsRaw) {
      if (normalizeType(r.typeRaw) !== "reviewee") continue;

      const rowData = {
        line: r.rowNumber,
        type: "reviewee",
        data: r,
        errors: [],
      };

      if (!r.firstName) rowData.errors.push("First name is required");
      if (!r.email) {
        rowData.errors.push("Email is required");
      } else if (!validateEmail(r.email)) {
        rowData.errors.push("Invalid email format");
      } else {
        const emailLower = r.email.toLowerCase();
        if (seenRevieweeEmails.has(emailLower)) {
          rowData.errors.push(`Duplicate email: ${r.email}`);
        }
      }

      if (rowData.errors.length > 0) {
        invalidRows.push(rowData);
        parseErrors.push(`Row ${r.rowNumber} (Reviewee): ${rowData.errors.join("; ")}`);
        continue;
      }

      const emailLower = r.email.toLowerCase();
      seenRevieweeEmails.add(emailLower);
      revieweeEmailMap.set(emailLower, true);
      reviewees.push({
        firstName: r.firstName,
        lastName: r.lastName,
        email: r.email,
      });
    }

    for (const r of rowsRaw) {
      if (normalizeType(r.typeRaw) !== "reviewer") {
        if (r.typeRaw && normalizeType(r.typeRaw) === null) {
          invalidRows.push({
            line: r.rowNumber,
            type: r.typeRaw,
            data: r,
            errors: [`Invalid type "${r.typeRaw}" — must be 'reviewee' or 'reviewer'`],
          });
          parseErrors.push(`Row ${r.rowNumber}: Invalid type "${r.typeRaw}"`);
        }
        continue;
      }

      const rowData = {
        line: r.rowNumber,
        type: "reviewer",
        data: r,
        errors: [],
      };

      if (!r.firstName) rowData.errors.push("First name is required");
      if (!r.email) {
        rowData.errors.push("Email is required");
      } else if (!validateEmail(r.email)) {
        rowData.errors.push("Invalid email format");
      } else {
        const emailLower = r.email.toLowerCase();
        if (seenReviewerEmails.has(emailLower)) {
          rowData.errors.push(`Duplicate email: ${r.email}`);
        }
      }
      if (!r.revieweeEmail) {
        rowData.errors.push("Reviewee email is required");
      } else if (!validateEmail(r.revieweeEmail)) {
        rowData.errors.push("Invalid reviewee email format");
      } else if (!revieweeEmailMap.has(r.revieweeEmail.toLowerCase())) {
        rowData.errors.push(`Reviewee "${r.revieweeEmail}" not found in reviewee rows`);
      }

      const relationshipNormalized = normalizeRelationship(r.relationshipType);
      if (!r.relationshipType) {
        rowData.errors.push("Relationship type is required");
      } else if (!relationshipNormalized) {
        rowData.errors.push(
          `Invalid relationship "${r.relationshipType}". Use Manager, Direct Report, Peer, or Other Colleague`,
        );
      }

      if (rowData.errors.length > 0) {
        invalidRows.push(rowData);
        parseErrors.push(`Row ${r.rowNumber} (Reviewer): ${rowData.errors.join("; ")}`);
        continue;
      }

      seenReviewerEmails.add(r.email.toLowerCase());
      reviewers.push({
        firstName: r.firstName,
        lastName: r.lastName,
        email: r.email,
        revieweeEmail: r.revieweeEmail.toLowerCase(),
        type: relationshipNormalized,
      });
    }

    return { reviewees, reviewers, errors: parseErrors, invalidRows };
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!/\.xlsx?$/i.test(file.name)) {
      setAlertMsg("Please select an .xlsx file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    try {
      const {
        reviewees,
        reviewers,
        errors: parseErrors,
        invalidRows: invalidRowsData,
      } = await parseXlsx(file);

      setParsedReviewees(reviewees);
      setParsedReviewers(reviewers);
      setErrors(parseErrors);
      setInvalidRows(invalidRowsData);
      setShowPreview(true);
      setTabValue(0);
    } catch (error) {
      console.error("xlsx parse error:", error);
      setAlertMsg("Error reading .xlsx file");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (parsedReviewees.length === 0) {
      setAlertMsg("No valid reviewee data to upload");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      return;
    }

    setLoading(true);
    try {
      const result = await createAndAddCombinedUsersData(groupId, {
        reviewees: parsedReviewees,
        reviewers: parsedReviewers,
        slug,
      });

      if (result.success) {
        setLoading(false);
        setAlertMsg(
          `Successfully added ${parsedReviewees.length} reviewee(s) and ${parsedReviewers.length} reviewer(s)`,
        );
        setAlertSeverity("success");
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
          handleClose();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setLoading(false);
        setAlertMsg(result.msg || "Failed to upload data");
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      }
    } catch (error) {
      setLoading(false);
      setAlertMsg("Error uploading data");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  const handleClose = () => {
    setParsedReviewees([]);
    setParsedReviewers([]);
    setErrors([]);
    setInvalidRows([]);
    setShowPreview(false);
    setShowAlert(false);
    setTabValue(0);
    onClose();
  };

  const hasValidData = parsedReviewees.length > 0 || parsedReviewers.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Upload 360 Assessment Data (.xlsx)</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Upload a single .xlsx file containing both reviewees and their reviewers.
            The type and relationship columns are dropdowns.
          </Typography>

          <Box display="flex" gap={2} sx={{ mb: 2 }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
            >
              Download Sample Template
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Select .xlsx File
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                ref={fileInputRef}
              />
            </Button>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            <strong>Format:</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            Reviewees: type=reviewee, firstName, lastName, email
          </Typography>
          <Typography variant="caption" color="textSecondary" component="div">
            Reviewers: type=reviewer, firstName, lastName, email, revieweeEmail,
            relationshipType (Manager / Direct Report / Peer / Other Colleague)
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Validation Warnings:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errors.slice(0, 10).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {errors.length > 10 && (
                <li>...and {errors.length - 10} more errors</li>
              )}
            </ul>
          </Alert>
        )}

        {showPreview && (hasValidData || invalidRows.length > 0) && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                aria-label="preview tabs"
              >
                <Tab label={`Reviewees (${parsedReviewees.length})`} id="tab-0" />
                <Tab label={`Reviewers (${parsedReviewers.length})`} id="tab-1" />
                {invalidRows.length > 0 && (
                  <Tab
                    label={`Invalid Rows (${invalidRows.length})`}
                    id="tab-2"
                    sx={{ color: "error.main" }}
                  />
                )}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {parsedReviewees.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>First Name</strong></TableCell>
                        <TableCell><strong>Last Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedReviewees.map((user, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">No reviewees to display</Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {parsedReviewers.length > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>First Name</strong></TableCell>
                        <TableCell><strong>Last Name</strong></TableCell>
                        <TableCell><strong>Email</strong></TableCell>
                        <TableCell><strong>Reviewee Email</strong></TableCell>
                        <TableCell><strong>Relationship</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedReviewers.map((user, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{user.firstName}</TableCell>
                          <TableCell>{user.lastName}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.revieweeEmail}</TableCell>
                          <TableCell>
                            {RELATIONSHIP_DISPLAY[user.type] || user.type}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">No reviewers to display</Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {invalidRows.length > 0 && (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {invalidRows.length} row(s) with errors:
                    </Typography>
                  </Alert>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#ffebee" }}>
                          <TableCell><strong>Row</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Email</strong></TableCell>
                          <TableCell><strong>Error(s)</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invalidRows.map((row, idx) => (
                          <TableRow key={idx} sx={{ backgroundColor: "#fff3e0" }}>
                            <TableCell>{row.line}</TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{ textTransform: "capitalize", fontWeight: "bold" }}
                              >
                                {row.type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {row.data?.email || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {row.errors.map((error, errIdx) => (
                                  <Typography
                                    key={errIdx}
                                    variant="caption"
                                    display="block"
                                    color="error"
                                    sx={{ mb: 0.5 }}
                                  >
                                    • {error}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </TabPanel>
          </>
        )}

        {showAlert && (
          <Alert severity={alertSeverity} sx={{ mt: 2 }}>
            {alertMsg}
          </Alert>
        )}

        <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 3 }}>
          {hasValidData && (
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={loading || parsedReviewees.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Uploading..." : "Upload Data"}
            </Button>
          )}
          <Button variant="outlined" onClick={handleClose} color="inherit">
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CsvUploadCombined360;
